import { inr, px, sign } from "@/lib/api";

export const Stat = ({ label, value, cls = "text-slate-100", testId, sub }) => (
  <div className="panel px-4 py-3" data-testid={testId}>
    <div className="label-xs mb-1">{label}</div>
    <div className={`num text-xl font-semibold ${cls}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

export const Side = ({ side }) => (
  <span className={`num text-[11px] px-1.5 py-0.5 rounded border ${side === "LONG" ? "border-emerald-500/40 up" : side === "SHORT" ? "border-red-500/40 down" : "border-slate-600 text-slate-400"}`}>
    {side || "NONE"}
  </span>
);

const KIND_COLOR = {
  signal: "text-cyan-300", shift: "text-cyan-300", fill: "text-slate-100", partial: "up", exit: "text-slate-100",
  skip: "text-amber-300", cancel: "text-amber-300", expire: "text-slate-400", rejected: "text-amber-300", missed: "text-slate-400", warmup: "text-cyan-300", alert: "down font-bold",
  break: "text-violet-300", trail: "text-slate-400", bias: "text-cyan-300", select: "text-cyan-300",
  broker: "text-violet-300", broker_error: "down", confirmed: "text-cyan-300", triggered: "text-cyan-300",
  margin: "down font-semibold", margin_ok: "text-slate-400", lowvol: "text-slate-400",
};

export function EventLog({ events = [], testId = "event-log", max = 300 }) {
  const list = [...events].reverse().slice(0, max);
  return (
    <div className="max-h-[360px] overflow-y-auto pr-1" data-testid={testId}>
      {!list.length && <div className="label-xs py-6 text-center">No events yet</div>}
      {list.map((e, i) => (
        <div key={i} className="flex gap-3 py-1.5 border-b border-[#1E293B]/60 text-xs">
          <span className="num text-slate-500 w-11 shrink-0">{e.t}</span>
          {e.sym && <span className="num text-slate-300 w-24 shrink-0 truncate">{e.sym}</span>}
          <span className={`${KIND_COLOR[e.kind] || "text-slate-300"} break-words min-w-0`}>{e.msg}</span>
        </div>
      ))}
    </div>
  );
}

export function TradesTable({ trades = [], showDate = false, testId = "trades-table", onRow, targetLabel = "2R" }) {
  if (!trades.length) return <div className="label-xs py-6 text-center" data-testid={`${testId}-empty`}>No trades</div>;
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left label-xs border-b border-[#1E293B]">
            {showDate && <th className="py-2 pr-3">Date</th>}
            <th className="py-2 pr-3">Stock</th><th className="pr-3">Side</th><th className="pr-3 text-right">Qty</th>
            <th className="pr-3 text-right">Entry</th><th className="pr-3 text-right">Stop</th><th className="pr-3 text-right">{targetLabel}</th>
            <th className="pr-3">Exits</th><th className="pr-3 text-right">R</th><th className="text-right">Net</th>
          </tr>
        </thead>
        <tbody>
          {trades.map((t, i) => (
            <tr key={i} onClick={() => onRow && onRow(t)} data-testid={`${testId}-row-${i}`}
              className={`border-b border-[#1E293B]/60 hover:bg-[#172033] ${onRow ? "cursor-pointer" : ""}`}>
              {showDate && <td className="py-2 pr-3 num text-slate-400">{t.date}</td>}
              <td className="py-2 pr-3 num text-slate-100">{t.sym}</td>
              <td className="pr-3"><Side side={t.side} /></td>
              <td className="pr-3 num text-right">{t.qty}</td>
              <td className="pr-3 num text-right">{px(t.entry)} <span className="text-slate-500">{t.t_in}</span></td>
              <td className="pr-3 num text-right">{px(t.init_stop)}</td>
              <td className="pr-3 num text-right">{px(t.target)}</td>
              <td className="pr-3 text-slate-400">{t.legs.map((l) => `${l.qty}@${px(l.px)} ${l.t} ${l.reason.replace(`${l.t} `, "")}`).join(" · ")}</td>
              <td className={`pr-3 num text-right whitespace-nowrap ${sign(t.r_mult)}`}>{t.r_mult}</td>
              <td className={`num text-right whitespace-nowrap ${sign(t.net)}`}>{inr(t.net)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
